import { useQuery } from '@tanstack/react-query';
import { BedDouble, Percent, DollarSign, TrendingUp, Wallet } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import StatCard from '@/components/admin/dashboard/StatCard';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';

/** Mirrors the `/admin/lodging/kpis` handler response (lodging-kpis.ts). */
interface Kpis {
  from: string;
  to: string;
  range_days: number;
  property_count: number;
  available_nights: number;
  booked_nights: number;
  revenue: number;
  occupancy_pct: number;
  adr: number;
  revpar: number;
  bookings_count: number;
  lead_time: { p25: number; p50: number; p75: number };
  channel_mix: Record<string, number>;
  cancellation_rate_30d: number;
}

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function LodgingKpisWidget() {
  const today = new Date();
  const fromDate = new Date(today);
  fromDate.setDate(fromDate.getDate() - 30);
  const from = ymd(fromDate);
  const to = ymd(today);

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'lodging', 'kpis', from, to],
    queryFn: () => api.get<Kpis>(`/admin/lodging/kpis?from=${from}&to=${to}`),
    staleTime: 60000,
  });

  return (
    <WidgetShell
      title="Lodging KPIs"
      icon={<BedDouble />}
      to="/admin/lodging"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && data.property_count === 0}
      emptyIcon={<BedDouble />}
      emptyMessage="No published properties yet."
      skeletonClassName="h-40"
    >
      {data && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard
            title="Occupancy"
            value={`${Math.round(data.occupancy_pct)}%`}
            icon={<Percent />}
            tone="green"
            subtitle={`${data.booked_nights} of ${data.available_nights} nights`}
          />
          <StatCard
            title="ADR"
            value={formatCurrency(data.adr)}
            icon={<DollarSign />}
            tone="blue"
            subtitle="Avg daily rate"
          />
          <StatCard
            title="RevPAR"
            value={formatCurrency(data.revpar)}
            icon={<TrendingUp />}
            tone="purple"
            subtitle="Per available night"
          />
          <StatCard
            title="Revenue"
            value={formatCurrency(data.revenue)}
            icon={<Wallet />}
            tone="teal"
            subtitle={`${data.bookings_count} bookings · ${data.property_count} properties`}
          />
        </div>
      )}
    </WidgetShell>
  );
}
